import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type ChildPowerChapter1Props = {
  data: ChildPowerData['importance_to_listen_to_children'];
};

export function ChildPowerChapter1(props: ChildPowerChapter1Props) {
  const { data } = props;

  const formatValue = useNumberFormatting('percent');
  const { i18n } = useLingui();

  const [chartData, setChartData] = useState<SunriseChartData>();

  const handleStepEnter = async (evt: OnStepEnterExitArgs<SunriseChartData>) => {
    if (evt.data) setChartData(evt.data);
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <SunriseChart data={chartData} formatValue={formatValue} />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter}>
        <ScrollStep
          data={[
            {
              label: t({
                id: 'child-power.chapter-1.chart-label',
                message:
                  '% who say it’s very important that political leaders listen to children when making decisions',
              }),
              data: [{ label: youngerGeneration(i18n), value: data.younger }],
            },
          ]}
        >
          <StoryText>
            <Trans id="child-power.chapter-1.text-1">
              On average, a majority of <PrimaryAnnotatedType>young people</PrimaryAnnotatedType>{' '}
              believe it’s very important for children’s voices to be heard.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              label: t({
                id: 'child-power.chapter-1.chart-label',
                message:
                  '% who say it’s very important that political leaders listen to children when making decisions',
              }),
              data: [{ label: youngerGeneration(i18n), value: data.younger }],
            },
            {
              data: [{ label: olderGeneration(i18n), value: data.older }],
            },
          ]}
        >
          <StoryText>
            <Trans id="child-power.chapter-1.text-2">
              That’s not too surprising. But it turns out that{' '}
              <SecondaryAnnotatedType>older people</SecondaryAnnotatedType> tend to agree with them!
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
